/**
 * lib/theme.ts
 * ------------------------------------------------------------------
 * Round 8 — dark mode support.
 *
 * Owns the app's theme selection. Three modes:
 *   - 'light'   force light
 *   - 'dark'    force dark
 *   - 'system'  follow OS preference (prefers-color-scheme)
 *
 * Persistence: localStorage under 'lkc.theme'. Default for new users
 * is 'system'.
 *
 * The `dark` class on <html> is what Tailwind's `dark:` variants key
 * off (darkMode: 'class' is set in tailwind.config.js). This module
 * is the single source of truth for adding/removing that class.
 *
 * A pre-paint script in index.html applies the same logic before
 * React mounts, to avoid a flash of light theme when a dark-mode
 * user loads the page. Keep this module's resolution logic and
 * the pre-paint script in sync if you change one.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'lkc.theme';

/** Read the user's saved preference. Returns 'system' if absent or invalid. */
export function getStoredTheme(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* localStorage blocked - fall through to default */
  }
  return 'system';
}

/** Persist the user's preference and update the DOM. */
export function setTheme(mode: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* localStorage blocked - still update the DOM in-memory */
  }
  applyTheme(mode);
}

/** Resolve a mode to the concrete light/dark choice that will be displayed. */
export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return mode;
}

/** Apply the given mode to the DOM (toggles the `dark` class on <html>). */
export function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const effective = resolveTheme(mode);
  if (effective === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

/**
 * React hook for theme state.
 *
 * Returns [mode, setMode, effective] where:
 *   - mode is the user's setting ('light' | 'dark' | 'system')
 *   - setMode persists + applies a new setting
 *   - effective is the concrete light/dark currently active
 *
 * When mode is 'system', the hook subscribes to prefers-color-scheme
 * changes so the UI updates if the user flips their OS dark mode
 * while the app is open.
 */
export function useTheme(): {
  mode: ThemeMode;
  setMode: (next: ThemeMode) => void;
  effective: 'light' | 'dark';
} {
  const [mode, setModeState] = useState<ThemeMode>(() => getStoredTheme());
  const [effective, setEffective] = useState<'light' | 'dark'>(() =>
    resolveTheme(getStoredTheme()),
  );

  // When mode === 'system', listen for OS-level changes
  useEffect(() => {
    if (mode !== 'system' || typeof window === 'undefined') {
      setEffective(resolveTheme(mode));
      return;
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      applyTheme('system');
      setEffective(mql.matches ? 'dark' : 'light');
    };
    setEffective(mql.matches ? 'dark' : 'light');
    // Safari < 14 only supports addListener; modern browsers prefer addEventListener.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Fallback for older browsers
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [mode]);

  function setMode(next: ThemeMode) {
    setTheme(next);
    setModeState(next);
    setEffective(resolveTheme(next));
  }

  return { mode, setMode, effective };
}
